import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import Text from "@components/Text";
import FieldRow from "@screens/FieldsScreen/FieldsMenu/FieldRow";
import {
  getFieldAreaHectares,
  useFieldsScreenVM,
} from "@screens/FieldsScreen/FieldsScreenVM";
import { observer } from "mobx-react-lite";
import { useFormatArea, useLang, useLength, useSettings } from "@src/hooks/useLang";
import { LengthUnit } from "@services/demosService";
import {
  convertLength,
  roundLengthForDisplay,
  roundLengthForStorage,
} from "@src/utils/convert";
import { useFarmsVM } from "@src/stores/FarmStore";
import SizedBox from "@components/SizedBox";
import { ReactComponent as BackIcon } from "@assets/icons/dubleArrowLeft.svg";
import Button from "@components/Button";
import Dialog from "@components/Dialog";
import { Column, Row } from "@src/components/Flex";
import { ReactComponent as DeleteIcon } from "@assets/icons/trash.svg";
import { ReactComponent as EditIcon } from "@assets/icons/edit.svg";
import Input from "@components/Input";
import Select from "@components/Select";
import Checkbox from "@components/Checkbox";
import { TFieldProperties } from "@services/fieldsService";

interface IProps {}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 0 16px;
`;

const StyledFieldRow = styled(FieldRow)`
  border-top: none;
  width: 100%;
  padding-left: 0;
  padding-right: 0;
  cursor: auto;
  :hover {
    background: #fff;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #494c5b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AddButton = styled.button`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #68ad18;
  background: none;
  border: 1px solid #68ad18;
  border-radius: 4px;
  padding: 3px 10px;
  cursor: pointer;
  &:hover {
    background: rgba(104, 173, 24, 0.08);
  }
`;

const ToolPicker = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0 8px;
`;

const ToolRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ToolButton = styled.button`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #494c5b;
  background: none;
  border: 1px solid #cbd5d8;
  border-radius: 4px;
  padding: 5px 12px;
  cursor: pointer;
  &:hover {
    border-color: #68ad18;
    color: #68ad18;
    background: rgba(104, 173, 24, 0.06);
  }
`;

const TrialRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f3;
  width: 100%;
  gap: 12px;
`;

const TrialInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TrialNameInput = styled.input`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  font-weight: 600;
  border: none;
  border-bottom: 1px solid transparent;
  background: none;
  padding: 2px 0;
  width: 100%;
  outline: none;
  cursor: text;
  &:hover {
    border-bottom-color: #cbd5d8;
  }
  &:focus {
    border-bottom-color: #68ad18;
  }
`;

const TrialArea = styled.div`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  color: #899093;
  margin-top: 2px;
  white-space: nowrap;
`;

const HelpText = styled.div`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  color: #899093;
  line-height: 1.4;
  padding: 4px 0;
`;

// "Completed" toggle row. 44px tap target on the checkbox keeps it comfortable
// on mobile web; the whole row is clickable.
const CompletedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  padding: 6px 0;

  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
`;

const CompletedLabel = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #494c5b;
`;

const CompletedHint = styled.div`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  color: #899093;
  line-height: 1.4;
`;

const EditFieldsMenu: React.FC<IProps> = () => {
  const lang = useLang();
  const formatArea = useFormatArea();
  const vm = useFieldsScreenVM();
  const farmsVM = useFarmsVM();
  const field = vm.selectedField;
  const properties = field?.getProperties() as TFieldProperties | undefined;
  const id = field?.getId();
  const geometry = field?.getGeometry();
  // original_name: the real name while the avg-yield overview has "Name (12.3)"
  // in `name` — never seed (and save) the label.
  const [name, setName] = useState<string>(properties?.original_name ?? properties?.name ?? "");
  const [cropId, setCropId] = useState<string>(properties?.culture_id || "");
  const [completed, setCompleted] = useState<boolean>(Boolean(properties?.completed));
  // An evened yield keeps the field completed: the server refuses to unmark it
  // (409 YIELD_EVENED) until the evening is removed.
  const evened = vm.selectedFieldEvened;
  // The list is farm-scoped, so the field being edited is in the current farm.
  const [farmId, setFarmId] = useState<string>(farmsVM.currentFarmId ?? "");
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [trialPickerOpen, setTrialPickerOpen] = useState(false);
  const [polygonExpanded, setPolygonExpanded] = useState(false);
  const handleSave = () =>
    vm.save({ name, cropId, farmId, completed: completed || evened });

  // The trial width is typed in the user's length unit, but vm.trialWidthMeters
  // stays metres — it feeds the map preview geometry and the API. Keep the typed
  // text in local state so converting on every keystroke doesn't fight the caret.
  const locLength = useLength();
  const { length: lengthUnit } = useSettings();
  const [trialWidthInput, setTrialWidthInput] = useState<string>("");

  // Re-seed the visible value when the VM's metres change from elsewhere (panel
  // opened, saved trial loaded for edit), but leave it alone when the change
  // came from this input.
  useEffect(() => {
    setTrialWidthInput((current) => {
      if (current !== "") {
        const currentMeters = roundLengthForStorage(
          convertLength(Number(current), lengthUnit, LengthUnit.Meter)
        );
        if (currentMeters === roundLengthForStorage(vm.trialWidthMeters)) {
          return current;
        }
      }
      return String(
        roundLengthForDisplay(
          convertLength(vm.trialWidthMeters, LengthUnit.Meter, lengthUnit)
        )
      );
    });
  }, [vm.trialWidthMeters, lengthUnit]);

  const closeTrialPicker = () => {
    setTrialPickerOpen(false);
    setPolygonExpanded(false);
  };
  const startTrial = (tool: "line" | "polygon" | "rectangle") => {
    closeTrialPicker();
    vm.handleAddTrial(tool);
  };

  return (
    <Root>
      <Column crossAxisSize="max" style={{ overflow: "auto", flex: 1 }}>
        <Row alignItems="center" style={{ cursor: "pointer" }}>
          <BackIcon style={{ marginRight: 4 }} />
          <Text size={12} color="#899093" onClick={vm.handleCancel}>
            {lang.fields.fieldEdintPointer}
          </Text>
        </Row>
        <SizedBox height={24} />
        <StyledFieldRow
          id={id}
          geometry={geometry}
          title={name}
          crop={vm.crops.find(({ id }) => id === cropId)?.name}
          fillColor={vm.crops.find(({ id }) => id === cropId)?.category.color}
          area={formatArea(getFieldAreaHectares(field))}
          isJohnDeereLinked={typeof properties?.externalId === "string" && properties.externalId.startsWith("johndeere:")}
          completed={completed || evened}
          extra={
            properties?.id && (
              <DeleteIcon
                style={{ flexShrink: 0, cursor: "pointer" }}
                onClick={() => setDeleteConfirmVisible(true)}
              />
            )
          }
        />
        <SizedBox height={16} />
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          label={lang.fields.newFieldName}
        />
        <SizedBox height={16} />
        <Select
          label={lang.fields.newFieldCrop}
          value={cropId}
          onChange={setCropId}
          options={vm.crops.map((crop) => ({
            title: crop.name,
            value: crop.id,
          }))}
        />
        {farmsVM.farms.length > 1 && (
          <>
            <SizedBox height={16} />
            <Select
              label={lang.menu.farmTitle}
              value={farmId}
              onChange={setFarmId}
              options={farmsVM.farms.map((f) => ({ title: f.name, value: f.id }))}
            />
          </>
        )}

        {/* ── Completed toggle ────────────────────────────── */}
        <SizedBox height={16} />
        <CompletedRow
          onClick={evened ? undefined : () => setCompleted((v) => !v)}
          aria-disabled={evened || undefined}
          style={evened ? { cursor: "default" } : undefined}
        >
          {/* Display-only: the row's onClick owns the toggle (the svg click
              bubbles up), so we don't pass onChange here — that would fire a
              second toggle and cancel the first. */}
          <span
            style={{
              display: "flex",
              flexShrink: 0,
              opacity: evened ? 0.5 : 1,
              pointerEvents: evened ? "none" : undefined,
            }}
          >
            <Checkbox checked={completed || evened} />
          </span>
          <Column style={{ minWidth: 0 }}>
            <CompletedLabel>
              {lang.fields.completedLabel}
            </CompletedLabel>
            <CompletedHint>
              {evened
                ? lang.fields.yieldEvenCompletedLocked
                : lang.fields.completedHint}
            </CompletedHint>
          </Column>
        </CompletedRow>

        {/* ── Trials Section ──────────────────────────────── */}
        <SizedBox height={24} />
        <SectionHeader>
          <SectionTitle>{lang.fields?.trialsSection ?? "Trials"}</SectionTitle>
          {!vm.isTrialDrawing && !trialPickerOpen && (
            <AddButton onClick={() => setTrialPickerOpen(true)}>
              + {lang.fields?.addTrialButton ?? "Add trial"}
            </AddButton>
          )}
        </SectionHeader>

        {!vm.isTrialDrawing && trialPickerOpen && (
          <ToolPicker>
            {!polygonExpanded ? (
              <ToolRow>
                <ToolButton onClick={() => startTrial("line")}>
                  {lang.fields?.trialToolLine ?? "Line"}
                </ToolButton>
                <ToolButton onClick={() => setPolygonExpanded(true)}>
                  {lang.fields?.trialToolPolygon ?? "Polygon"}
                </ToolButton>
                <ToolButton onClick={closeTrialPicker}>
                  {lang.fields?.cancelButton ?? "Cancel"}
                </ToolButton>
              </ToolRow>
            ) : (
              <ToolRow>
                <ToolButton onClick={() => startTrial("polygon")}>
                  {lang.fields?.trialToolByHand ?? "Draw by hand"}
                </ToolButton>
                <ToolButton onClick={() => startTrial("rectangle")}>
                  {lang.fields?.trialToolRectangle ?? "Rectangle"}
                </ToolButton>
                <ToolButton onClick={() => setPolygonExpanded(false)}>
                  {lang.fields?.backButton ?? "Back"}
                </ToolButton>
              </ToolRow>
            )}
          </ToolPicker>
        )}

        {vm.isTrialDrawing ? (
          <Column crossAxisSize="max" style={{ gap: 8 }}>
            {!vm.trialPointsPlaced ? (
              <HelpText>
                {vm.trialDrawTool === "rectangle"
                  ? lang.fields?.trialRectangleHelp ??
                    "Click and drag on the map to draw a rectangle."
                  : vm.trialDrawTool === "polygon"
                  ? lang.fields?.trialPolygonHelp ??
                    "Click on the map to add points, then double-click to finish the shape."
                  : lang.fields?.trialDrawHelp ??
                    "Click two points on the map to define the trial center-line."}
              </HelpText>
            ) : (
              <>
                <Input
                  value={vm.trialName}
                  onChange={(e) => vm.setTrialName(e.target.value)}
                  label={lang.fields?.trialNameLabel ?? "Variety / trial name"}
                />
                {vm.trialDrawTool === "line" && (
                  <>
                    <SizedBox height={4} />
                    <Input
                      value={trialWidthInput}
                      onChange={(e) => {
                        setTrialWidthInput(e.target.value);
                        const n = parseFloat(e.target.value);
                        if (!isNaN(n) && n > 0) {
                          vm.setTrialWidth(
                            roundLengthForStorage(
                              convertLength(n, lengthUnit, LengthUnit.Meter)
                            )
                          );
                        }
                      }}
                      label={`${lang.fields?.trialWidthLabel ?? "Width"} (${locLength()})`}
                      type="number"
                    />
                  </>
                )}
                <SizedBox height={8} />
                <Row>
                  <Button kind="outline" onClick={vm.cancelTrialDraw}>
                    {lang.fields?.cancelButton ?? "Cancel"}
                  </Button>
                  <SizedBox width={8} />
                  <Button onClick={vm.saveTrial}>
                    {lang.fields?.saveTrialButton ?? "Save trial"}
                  </Button>
                </Row>
              </>
            )}
          </Column>
        ) : (
          <Column crossAxisSize="max">
            {vm.selectedFieldTrials.length === 0 ? (
              <HelpText>
                {lang.fields?.noTrials ?? "No trials yet. Click '+ Add trial' to create one."}
              </HelpText>
            ) : (
              vm.selectedFieldTrials.map((trial) => (
                <TrialRow key={trial.id}>
                  <TrialInfo>
                    <TrialNameInput
                      // Uncontrolled so typing doesn't round-trip through the VM;
                      // key on the name so a rename (or a reload) re-seeds it.
                      key={trial.name}
                      defaultValue={trial.name}
                      placeholder={lang.fields?.trialNameLabel ?? "Variety / trial name"}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val && val !== trial.name) {
                          vm.renameTrial(trial.id, val);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                      }}
                    />
                    <TrialArea>{formatArea(trial.areaHectares)}</TrialArea>
                  </TrialInfo>
                  {/* mainAxisSize="fit-content": Row defaults to width:100%, which
                      with flex-shrink:0 would eat the whole line and collapse the
                      name input next to it to zero width. */}
                  <Row
                    alignItems="center"
                    mainAxisSize="fit-content"
                    style={{ gap: 10, flexShrink: 0 }}
                  >
                    <EditIcon
                      style={{ cursor: "pointer", width: 16, height: 16 }}
                      onClick={() => vm.editTrialShape(trial)}
                      title={lang.fields?.editTrialShape ?? "Edit shape"}
                    />
                    <DeleteIcon
                      style={{ cursor: "pointer", width: 16, height: 16 }}
                      onClick={() => vm.deleteTrialById(trial.id)}
                    />
                  </Row>
                </TrialRow>
              ))
            )}
          </Column>
        )}
      </Column>

      {!vm.isTrialDrawing && (
        <Row style={{ paddingTop: 12 }}>
          <Button kind="outline" onClick={vm.handleCancel}>
            {lang.fields.drawFieldsCancelButton}
          </Button>
          <SizedBox width={8} />
          <Button onClick={handleSave}>{lang.fields.drawFieldsOkButton}</Button>
        </Row>
      )}
      <Dialog
        title={lang.fields.deleteFieldTitle}
        visible={deleteConfirmVisible}
        onClose={() => setDeleteConfirmVisible(false)}
        style={{ maxWidth: 400 }}
      >
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Text size={14} color="#6B7280">
            {lang.fields.deleteFieldConfirm}
          </Text>
          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button kind="outline" onClick={() => setDeleteConfirmVisible(false)}>
              {lang.fields.drawFieldsCancelButton}
            </Button>
            <Button
              onClick={() => {
                setDeleteConfirmVisible(false);
                if (id) vm.deleteField(id);
              }}
              style={{ background: "#e53e3e", borderColor: "#e53e3e" }}
            >
              {lang.fields.deleteFieldButton}
            </Button>
          </Row>
        </Column>
      </Dialog>
    </Root>
  );
};
export default observer(EditFieldsMenu);
